/**
 * Storico Instagram/Messenger di un cliente nella cronologia della scheda.
 * Legge le conversazioni Meta collegate al cliente (meta_conversations.client_id)
 * e i loro messaggi; la RLS mostra solo le pagine a cui l'utente ha accesso.
 * Stessa sessionizzazione delle chat WhatsApp. Errori soft: mai un throw.
 * `clientId` null disattiva l'hook.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { useUserData } from '../auth';
import { sessionizeMessages } from '../utils/whatsappSessions';

export type MetaChannel = 'instagram' | 'messenger';

export interface ClientMetaMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  timestamp: Date;
  content: string;
}

export interface ClientMetaSession {
  key: string;
  conversationId: string;
  channel: MetaChannel;
  contactName: string;
  contactHandle: string;
  profilePicUrl: string | null;
  startAt: string;
  endAt: string;
  messageCount: number;
  inboundCount: number;
  outboundCount: number;
  lastPreview: string;
  messages: ClientMetaMessage[];
}

const ATTACHMENT_LABEL: Record<string, string> = {
  image: '[Immagine]', video: '[Video]', audio: '[Messaggio vocale]', file: '[Allegato]',
  reel: '[Reel]', story_mention: '[Menzione in una storia]', share: '[Contenuto condiviso]',
};

function previewOf(content: string, attachments: unknown): string {
  if (content && content.trim()) return content.trim();
  const list = Array.isArray(attachments) ? attachments : [];
  if (list.length === 0) return '';
  const first = list[0] as { type?: string };
  return ATTACHMENT_LABEL[first?.type ?? ''] ?? '[Allegato]';
}

export function useClientMetaConversations(clientId: string | null) {
  const { organizationId } = useUserData();
  const [sessions, setSessions] = useState<ClientMetaSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!clientId || !organizationId) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const { data: convs, error: convError } = await supabase
        .from('meta_conversations')
        .select('id, channel, contact_name, profile_username, profile_pic_url, last_message_at')
        .eq('organization_id', organizationId)
        .eq('client_id', clientId);
      if (convError) throw convError;
      const conversations = (convs ?? []) as Array<{
        id: string; channel: MetaChannel; contact_name: string | null; profile_username: string | null;
        profile_pic_url: string | null; last_message_at: string | null;
      }>;
      if (conversations.length === 0) {
        if (requestIdRef.current === requestId) setSessions([]);
        return;
      }
      const { data: msgs, error: msgError } = await supabase
        .from('meta_messages')
        .select('id, conversation_id, direction, content, sent_at, attachments')
        .in('conversation_id', conversations.map((c) => c.id))
        .order('sent_at', { ascending: true })
        .limit(1000);
      if (msgError) throw msgError;
      const byConv = new Map<string, Array<ClientMetaMessage & { attachments: unknown }>>();
      for (const m of (msgs ?? []) as Array<{ id: string; conversation_id: string; direction: 'inbound' | 'outbound'; content: string | null; sent_at: string; attachments: unknown }>) {
        const list = byConv.get(m.conversation_id) ?? [];
        list.push({
          id: m.id, direction: m.direction, timestamp: new Date(m.sent_at),
          content: previewOf(m.content ?? '', m.attachments), attachments: m.attachments,
        });
        byConv.set(m.conversation_id, list);
      }
      const result: ClientMetaSession[] = [];
      for (const conv of conversations) {
        const contactName = conv.contact_name || (conv.profile_username ? `@${conv.profile_username}` : (conv.channel === 'instagram' ? 'Contatto Instagram' : 'Contatto Messenger'));
        for (const group of sessionizeMessages(byConv.get(conv.id) ?? [])) {
          const messages: ClientMetaMessage[] = group.messages.map(({ id, direction, timestamp, content }) => ({ id, direction, timestamp, content }));
          const inbound = messages.filter((m) => m.direction === 'inbound').length;
          result.push({
            key: `${conv.id}|${group.startAt.toISOString()}`,
            conversationId: conv.id,
            channel: conv.channel,
            contactName,
            contactHandle: conv.profile_username && conv.contact_name ? `@${conv.profile_username}` : '',
            profilePicUrl: conv.profile_pic_url,
            startAt: group.startAt.toISOString(),
            endAt: group.endAt.toISOString(),
            messageCount: messages.length,
            inboundCount: inbound,
            outboundCount: messages.length - inbound,
            lastPreview: messages[messages.length - 1]?.content ?? '',
            messages,
          });
        }
      }
      if (requestIdRef.current !== requestId) return;
      setSessions(result);
    } catch (err) {
      console.error('[useClientMetaConversations]', err);
      if (requestIdRef.current === requestId) setError(true);
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, [clientId, organizationId]);

  useEffect(() => {
    requestIdRef.current += 1;
    setSessions([]);
    setError(false);
    void load();
  }, [load]);

  return { sessions, loading, error, refresh: load };
}
